import swipeDetect from 'swipe-detect';
import {initThumbsCarousel} from './Carousel';

class Slider {

	static _defaults = {
		selectors: {
			root: '.mdl_slider',
			track: '.mdl_slider__track',
			slides: '.mdl_slider__slide',
			controls: {
				root: '.mdl_slider__arrows',
				prev: '.mdl_slider__arrow-prev',
				next: '.mdl_slider__arrow-next',
			},
		},
		settings: {
			autoPlayOn: false,
			autoPlayPause: 5000,
		}
	};

	constructor( sliderEl, selectors = {}, settings = {} ) {
		this.selectors = Object.assign( {}, Slider._defaults.selectors, selectors );
		this.settings = Object.assign( {}, Slider._defaults.settings, settings );

		this.rootEl = sliderEl;
		if( !this.rootEl ) return;

		this.track = this.rootEl.querySelector( this.selectors.track );
		this.slides = this.rootEl.querySelectorAll( this.selectors.slides );
		if( !this.track || !this.slides || this.slides.length < 2 ) return;

		// Elements
		this.controlsRoot = this.rootEl.querySelector( this.selectors.controls.root );
		if( this.controlsRoot ) {
			this.controlsPrev = this.controlsRoot.querySelector( this.selectors.controls.prev );
			this.controlsNext = this.controlsRoot.querySelector( this.selectors.controls.next );
		}

		this.pagerRoot = this.rootEl.querySelector( '.mdl_slider__pager' );
		if( this.pagerRoot ) {
			this.pagerItems = this.pagerRoot.querySelectorAll( '.mdl_slider__pager-item' );
			this.pagerItemButtons = this.pagerRoot.querySelectorAll( '.mdl_slider__pager-item-link' );
		}

		this.thumbnailsRoot = false;

		// Settings
		this.settings.autoPlayOn = this.rootEl.dataset.auto === 'true' || this.settings.autoPlayOn;
		this.settings.autoPlayPause = this.rootEl.dataset.pause || this.settings.autoPlayPause;

		// Variables
		this.hasControls = this.controlsRoot && this.controlsRoot && this.controlsRoot;
		this.hasSmartBullets = this.pagerRoot && this.pagerItems && this.pagerItemButtons;
		this.currentSlide = this.slides[0];
		this.lastSlide = this.currentSlide;
		this.currentSlideIndex = 0;
		this.slidesCount = this.slides.length -1;
		this.autoPlayInterval = false;
		this.locked = false;

		// Functions
		this.controlsLogic();
		this.smartBulletsLogic();
		this.thumbsLogic();
		this.swipeLogic();
		this.initAutoPlay();
	}

	controlsLogic() {
		if( this.hasControls ) {
			this.controlsPrev.addEventListener( 'click', () => {
				this.move( 'prev' )
			} );
			this.controlsNext.addEventListener( 'click', () => {
				this.move( 'next' )
			} );
		}
	}

	smartBulletsLogic() {
		if( this.hasSmartBullets ) {
			this.pagerItemButtons.forEach( ( pagerItemButton ) => {
				pagerItemButton.addEventListener( 'click', ( e ) => {
					e.preventDefault();
					this.move( 'goto', 'fade', pagerItemButton.dataset.slideIndex );
				} );
			} );
		}
	}

	thumbsLogic() {
		if( this.rootEl.dataset.sync ) {
			this.thumbnailsRoot = document.getElementById( this.rootEl.dataset.sync );
			if( this.thumbnailsRoot ) {
				initThumbsCarousel( this.thumbnailsRoot );
				this.rootEl.addEventListener( 'syncThumbs', e => {
					this.move( 'goto', 'fade', e.detail, false );
				} );
			}
		}
	}

	swipeLogic() {
		swipeDetect( this.track, ( direction ) => {
			if( direction === 'left' ) {
				this.move( 'next', 'scroll' );
			} else if( direction === 'right' ) {
				this.move( 'prev', 'scroll' );
			}
		}, 10 );
	}

	initAutoPlay() {
		if ( this.settings.autoPlayOn ) {
			this.updateAutoPlay();

			this.rootEl.addEventListener( 'mouseenter', () => this.updateAutoPlay() );
			this.rootEl.addEventListener( 'mouseleave', () => this.updateAutoPlay() );
			this.rootEl.addEventListener( 'focusin', () => this.updateAutoPlay() );
			this.rootEl.addEventListener( 'focusout', () => this.updateAutoPlay() );
		}
	}

	updateAutoPlay() {
		clearInterval( this.autoPlayInterval );
		if ( this.rootEl.matches( ':hover' ) || this.rootEl.matches( ':focus-within' ) ) {
			// Stop autoplay, while hovering or focused.
			return;
		}

		// Start Auto Play.
		this.autoPlayInterval = setInterval( () => {
			this.move();
		}, this.settings.autoPlayPause );
	}

	move( direction = 'next', type = 'fade', gotoSlideNr = false, syncThumbs = true ) {
		gotoSlideNr = parseInt( gotoSlideNr );
		if( this.locked || ( direction === 'goto' && this.currentSlideIndex === gotoSlideNr ) ) return;

		this.locked = true;
		this.lastSlide = this.currentSlide;
		let outClass = 'slideshow-fadeout';
		let inClass = 'slideshow-fadein';
		let activeClass = 'is-active';

		if( type === 'scroll' && direction === 'next' ) {
			outClass = 'slideshow-scroll-forward-out';
			inClass = 'slideshow-scroll-forward-in';
		}

		if( type === 'scroll' && direction === 'prev' ) {
			outClass = 'slideshow-scroll-backward-out';
			inClass = 'slideshow-scroll-backward-in';
		}

		if( type === 'fade' ) {
			this.lastSlide.addEventListener( 'animationend', () => {
				this.lastSlide.classList.remove( ...[ activeClass, outClass ]  );
			}, { once: true } );
			this.lastSlide.classList.add( outClass );
		} else if( type === 'scroll' ) {
			this.currentSlide.addEventListener( 'animationend', () => {
				this.lastSlide.classList.remove( ...[ activeClass, outClass ]  );
			} ,{ once: true } );
			this.currentSlide.classList.add( outClass );
		}

		if( direction === 'next' ) {
			this.currentSlideIndex = this.currentSlideIndex === this.slidesCount ? 0 : this.currentSlideIndex + 1;
		} else if( direction === 'prev' ) {
			this.currentSlideIndex = this.currentSlideIndex === 0 ? this.slidesCount : this.currentSlideIndex - 1;
		} else if( direction === 'goto' ) {
			this.currentSlideIndex = parseInt( gotoSlideNr );
		}
		this.currentSlide = this.slides[ this.currentSlideIndex ];

		if( type === 'fade' || type === 'scroll' ) {
			this.currentSlide.addEventListener( 'animationend', () => {
				this.currentSlide.classList.remove( inClass );
				this.locked = false;
			} ,{ once: true } );
			this.currentSlide.classList.add( ...[ activeClass, inClass ] );

			if( type === 'fade' && direction === 'goto' ) {
				this.currentSlide.classList.add( inClass );
			}
		}

		// Restart autoplay if desired
		if( this.settings.autoPlayOn ) {
			this.updateAutoPlay();
		}

		if( this.hasSmartBullets ) {
			this.pagerItemButtons.forEach( ( pagerItemButtonEl, buttonIndex ) => {
				const isActive = buttonIndex === this.currentSlideIndex;
				pagerItemButtonEl.parentNode.classList.toggle( activeClass, isActive );
				pagerItemButtonEl.ariaDisabled = !isActive;
			} );

			this.pagerRoot.dataset.index = this.currentSlideIndex;
		}

		if( this.thumbnailsRoot && syncThumbs ) {
			this.thumbnailsRoot.dispatchEvent( new CustomEvent( 'syncThumbs', { detail: {
				direction: direction,
				slideIndex: this.currentSlideIndex
			} } ) );
		}
	}
}

const initSlider = function( elem = '.mdl_slider', selectors = {}, settings = {}, scopeEl = document ) {
	const sliderEls = scopeEl.querySelectorAll( elem );

	if ( sliderEls ) {
		sliderEls.forEach( sliderEl => {
			sliderEl.SL_Slider ??= new Slider( sliderEl, selectors, settings );
		} );
	}
}

const initDefaultSlider = function( scopeEl = document, elem = '.mdl_slider' ) {
	initSlider(
		elem,
		{},
		{},
		scopeEl
	);
}

export {
	Slider as default,
	initSlider,
	initDefaultSlider
}
